// PropertyProfileSection — May 6, 2026
// Renders the Regrid-derived parcel data for the deal in the Property tab.
// Parcel resolution: deal_parcel_for(deal_id) RPC follows knock_pin_id →
// knock_pins.parcel_id (preferred), then falls back to nearest parcel within
// 100m of deal.property_lat/lon.
//
// Skips the section silently if no parcel resolves — common for deals
// outside the counties we've ingested so far (Habersham, Hall, Pickens,
// and any non-metro Atlanta) until those Regrid files land.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";

interface ParcelRow {
  id: string;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  county: string | null;
  owner_name: string | null;
  owner_type: string | null;
  year_built: number | null;
  building_sqft: number | null;
  footprint_sqft: number | null;
  stories: number | null;
  estimated_squares: number | null;
  lot_sqft: number | null;
  last_sale_date: string | null;
  last_sale_price: number | null;
  use_code: string | null;
  qualifies: boolean | null;
  is_preferred: boolean | null;
  raw: any;
}

export default function PropertyProfileSection({ dealId }: { dealId: string }) {
  const [parcel, setParcel] = useState<ParcelRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase.rpc("deal_parcel_for", { p_deal_id: dealId });
      if (cancelled) return;
      if (error) { setErr(error.message); setLoading(false); return; }
      // RPC returns SETOF parcels — supabase-js gives an array OR an object
      // depending on the function definition. Normalize.
      let row: any = null;
      if (Array.isArray(data) && data.length > 0) row = data[0];
      else if (data && typeof data === "object" && (data as any).id) row = data;
      setParcel(row && row.id ? (row as ParcelRow) : null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dealId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Property Profile</div>
        <div className="mt-2 text-sm text-slate-500">Loading parcel data…</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">⚠ {err}</div>
    );
  }

  if (!parcel) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
        No parcel data on file for this property.
        <span className="ml-1 text-slate-500">
          Either the county isn't in the Regrid ingest yet (Habersham, Hall, Pickens still pending) or
          the property's coordinates don't fall within 100 m of any known parcel.
        </span>
      </div>
    );
  }

  // Pull out high-signal raw fields for display
  const raw = parcel.raw ?? {};
  const numBeds = raw.num_bedrooms;
  const numBaths = raw.num_bath;
  const flood = raw.fema_flood_zone;
  const subdivision = raw.subdivision;
  const dpv = raw.dpv_status;
  const vacancy = raw.usps_vacancy === "Y";

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Property Profile</div>
        <div className="flex items-center gap-2">
          {parcel.is_preferred ? (
            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-800">⭐ Preferred</span>
          ) : parcel.qualifies ? (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">✓ Qualifies</span>
          ) : (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">Outside knock filter</span>
          )}
          <span className="font-mono text-[10px] text-slate-400">REGRID</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 p-4 text-sm sm:grid-cols-3">
        <Pf label="Year built" value={parcel.year_built ?? "—"}
            hint={parcel.year_built ? `${new Date().getFullYear() - parcel.year_built} years` : null} />
        <Pf label="Stories" value={parcel.stories ?? "—"} />
        <Pf label="Building sqft" value={fmtInt(parcel.building_sqft)} />
        <Pf label="Footprint sqft" value={fmtInt(parcel.footprint_sqft)} />
        <Pf label="Est. roof squares" value={parcel.estimated_squares ?? "—"}
            hint="footprint × 1.15 ÷ 100" />
        <Pf label="Lot size" value={parcel.lot_sqft ? `${fmtInt(parcel.lot_sqft)} sf` : "—"}
            hint={parcel.lot_sqft ? `${(parcel.lot_sqft / 43560).toFixed(2)} acres` : null} />
        <Pf label="Owner" value={parcel.owner_name ?? "—"}
            hint={parcel.owner_type ? parcel.owner_type : null} />
        <Pf label="Last sale" value={parcel.last_sale_date ?? "—"}
            hint={parcel.last_sale_price ? `$${Number(parcel.last_sale_price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null} />
        <Pf label="Use code" value={parcel.use_code ?? "—"} />

        {numBeds || numBaths ? (
          <Pf label="Beds / Baths" value={[numBeds, numBaths].filter(Boolean).join(" / ") || "—"} />
        ) : null}
        {flood ? <Pf label="Flood zone" value={flood} hint={raw.fema_nri_risk_rating ?? null} /> : null}
        {subdivision ? <Pf label="Subdivision" value={subdivision} /> : null}
        {dpv ? <Pf label="USPS deliverability" value={dpv === "V" ? "✓ Valid" : dpv}
                  hint={vacancy ? "⚠ Currently vacant" : null} /> : null}
      </div>

      <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[10px] text-slate-500">
        Parcel ID <span className="font-mono">{parcel.id}</span>
        {parcel.county ? <span className="ml-2">· {parcel.county} County, GA</span> : null}
      </div>
    </div>
  );
}

function fmtInt(n: number | null | undefined): string {
  if (n == null) return "—";
  return Math.round(n).toLocaleString();
}

function Pf({ label, value, hint }: { label: string; value: string | number; hint?: string | null }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 font-medium text-slate-900">{value}</div>
      {hint ? <div className="text-[10px] text-slate-500">{hint}</div> : null}
    </div>
  );
}
